import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Typography,
  Box,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCyclePhase } from "../../redux/slices/powerCycleSlice";

const statusColors = {
  ONLINE: "success",
  OFFLINE: "error",
};

const phaseLabels = {
  initiated: "Cycling...",
  power_off: "Power off",
  device_offline: "Offline",
  complete: "Complete!",
};

const DeviceListRow = ({ device, isSelected, onSelect, onClick }) => {
  const cyclePhase = useSelector(selectCyclePhase(device.serial_number));
  const isCycling = !!cyclePhase && cyclePhase !== "complete";

  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{ cursor: "pointer", position: "relative" }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ position: "relative" }}>
          {isCycling && (
            <LinearProgress
              color="warning"
              sx={{
                position: "absolute",
                top: -8,
                left: -16,
                right: -16,
                height: 2,
              }}
            />
          )}
          <Typography variant="body2" fontWeight={500}>
            {device.name || device.serial_number}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {device.serial_number}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {device.location || "—"}
        </Typography>
      </TableCell>
      <TableCell>
        {cyclePhase ? (
          <Chip
            label={phaseLabels[cyclePhase] || cyclePhase}
            size="small"
            color={cyclePhase === "complete" ? "success" : "warning"}
          />
        ) : (
          device.status && (
            <Chip
              label={device.status}
              size="small"
              color={statusColors[device.status] || "default"}
            />
          )
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {device.firmware_version || "—"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const DeviceListView = ({
  devices,
  selectedDevices,
  onSelectDevice,
  isLoading,
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (!devices?.length) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" color="text.secondary">
          No devices found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Name</TableCell>
            <TableCell>Serial Number</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Firmware</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => (
            <DeviceListRow
              key={device.serial_number}
              device={device}
              isSelected={selectedDevices.includes(device.serial_number)}
              onSelect={() => onSelectDevice(device.serial_number)}
              onClick={() => navigate(`/devices/${device.serial_number}`)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DeviceListView;
